import {Component, inject, input, OnInit, viewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {PageGridComponent} from 'src/app/shared/components/page-grid/page-grid.component';
import {FeatureService} from 'src/infrastructure/services/feature.service';
import {DatePipe} from '@angular/common';
import {tap} from 'rxjs';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {GridModel} from 'src/app/shared/model/page-grid';
import {AdxInvoiceErpAdjustment} from '../../model/adx-invoice-erp-adjustment';
import {AdxInvoiceErpAdjustmentGridService} from '../../service/adx-invoice-erp-adjustment-grid.service';
import {SIMULATE_ERP_RESPONSE_FEATURE_FLAG} from '../../service/adx-invoice-erp-adjustment-simulation.service';
import {AddErpAdjustmentDialogComponent} from '../add-erp-adjustment-dialog/add-erp-adjustment-dialog.component';

@Component({
    selector: 'adx-invoice-erp-adjustment-list',
    templateUrl: './adx-invoice-erp-adjustment-list.component.html',
    standalone: false
})

@AutoUnsubscribe()
export class AdxInvoiceErpAdjustmentListComponent implements OnInit {
  public data = input<any>();

  private adxInvoiceErpAdjustmentGridService = inject(AdxInvoiceErpAdjustmentGridService);
  private datePipe = inject(DatePipe);
  private dialog = inject(MatDialog);
  private featureService = inject(FeatureService);
  private grid = viewChild(PageGridComponent);
  private refundProgressByBookingId = new Map<number, {refunded: number; original: number}>();

  protected readonly adxInvoiceErpAdjustmentsGrid: GridModel<AdxInvoiceErpAdjustment> = {
    gridDataTestId: 'adx-invoice-erp-adjustment-list',
    gridTitle: 'ERP Adjustments',
    isTotalsRowHidden: true,
    replayButton: true,
    defaultSort: {active: 'dateCreated', direction: 'desc'},
    columns: [
      {
        columnProperty: 'adxInvoiceErpAdjustmentTypeName',
        columnHeader: 'Type',
      },
      {
        columnProperty: 'uniqueLineId',
        columnHeader: 'Unique Line Id',
      },
      {
        columnProperty: 'itemNumber',
        columnHeader: 'Item Number',
      },
      {
        columnProperty: 'costPrice',
        columnHeader: 'Cost Price',
        columnPropertyFunction: (item: AdxInvoiceErpAdjustment) =>
          item.costPrice != null ? `${item.costCurrency ?? ''} ${item.costPrice.toFixed(2)}`.trim() : '',
      },
      {
        columnProperty: 'salesPrice',
        columnHeader: 'Sales Price',
        columnPropertyFunction: (item: AdxInvoiceErpAdjustment) =>
          item.salesPrice != null ? `${item.salesCurrency ?? ''} ${item.salesPrice.toFixed(2)}`.trim() : '',
      },
      {
        columnProperty: 'invoiceNumber',
        columnHeader: 'ERP Invoice Number',
      },
      {
        columnProperty: 'adxBookingId',
        columnHeader: 'Booking',
        columnPropertyFunction: (item: AdxInvoiceErpAdjustment) =>
          item.adxBookingId != null ? `Booking #${item.adxBookingId}` : '',
      },
      {
        columnProperty: 'refundProgress',
        columnHeader: 'Refund Progress',
        columnPropertyFunction: (item: AdxInvoiceErpAdjustment) => this.getRefundProgressText(item),
      },
      {
        columnProperty: 'originSystem',
        columnHeader: 'Origin System',
      },
      {
        columnProperty: 'applyStatusName',
        columnHeader: 'Status',
      },
      {
        columnProperty: 'errorMessage',
        columnHeader: 'Error Message',
      },
      {
        columnProperty: 'dateCreated',
        columnHeader: 'Date Created',
        columnPropertyFunction: (item: AdxInvoiceErpAdjustment) => this.datePipe.transform(item.dateCreated, 'M/d/y') || '',
        sort: true,
      },
    ],
    filters: [],
    // Refund progress is derived from the same rows the grid loads, so a refresh updates both.
    gridGetService: {
      getItems: (params) => this.adxInvoiceErpAdjustmentGridService.getItems(params)
        .pipe(tap(response => this.calculateRefundProgress(response.items))),
    },
  };

  public ngOnInit(): void {
    const dataValue = this.data();

    this.adxInvoiceErpAdjustmentsGrid.filtersInitial = {
      tripId: dataValue?.tripId || null,
    };

    // ERP response simulation is only exposed when its feature flag is on (off in Production).
    this.featureService.isEnabledFeature(SIMULATE_ERP_RESPONSE_FEATURE_FLAG).subscribe(enabled => {
      this.adxInvoiceErpAdjustmentsGrid.eventButton = enabled
        ? {text: 'Add Adjustment', icon: 'add'}
        : undefined;
    });
  }

  protected openAddAdjustmentDialog(): void {
    const tripId = this.data()?.tripId;

    this.dialog.open(AddErpAdjustmentDialogComponent, {
      data: {tripId},
      width: '560px',
    }).afterClosed().subscribe(created => {
      if (created) {
        this.grid()?.getItems();
      }
    });
  }

  private calculateRefundProgress(items: AdxInvoiceErpAdjustment[]): void {
    this.refundProgressByBookingId.clear();

    for (const item of items || []) {
      if (item.adxBookingId == null) {
        continue;
      }

      const entry = this.refundProgressByBookingId.get(item.adxBookingId) ?? {refunded: 0, original: item.originalAmount ?? 0};
      entry.refunded += Math.abs(item.salesPrice ?? 0);
      entry.original = item.originalAmount ?? entry.original;
      this.refundProgressByBookingId.set(item.adxBookingId, entry);
    }
  }

  private getRefundProgressText(item: AdxInvoiceErpAdjustment): string {
    if (item.adxBookingId == null) {
      return '';
    }

    const progress = this.refundProgressByBookingId.get(item.adxBookingId);

    if (!progress || !progress.original) {
      return '';
    }

    const percent = Math.round((progress.refunded / progress.original) * 100);

    return `$${progress.refunded.toFixed(2)} of $${progress.original.toFixed(2)} Refunded (${percent}%)`;
  }
}
